'use client'

import { useId, useState } from 'react'
import { Bookmark, Link as LinkIcon, Check } from 'lucide-react'
import { COLOR_TRAIT, FAVORITES_TRAIT, type DiscoveryFilter } from '@/utils/nodemonkes'
import { useSavedMonkes } from '@/utils/hooks/useMonkeDiscovery'

type Props = { filters: DiscoveryFilter[]; onChange: (type: string, values: string[]) => void }

export default function MonkeDiscoveryControls(props: Props) {
	const selected = props.filters.find((f) => f.trait_type === COLOR_TRAIT)?.values || []
	// Reset the draft when another filter drawer or a shared URL changes color.
	return <DiscoveryControls key={selected.join(',')} {...props} />
}

function DiscoveryControls({ filters, onChange }: Props) {
	const { saved } = useSavedMonkes()
	const [copied, setCopied] = useState(false)
	const [error, setError] = useState('')
	const selectedColors = filters.find((f) => f.trait_type === COLOR_TRAIT)?.values || []
	const [color, setColor] = useState<string | null>(selectedColors[0] ? `#${selectedColors[0]}` : null)
	const colorId = useId()
	const savedOnly = filters.some((f) => f.trait_type === FAVORITES_TRAIT)
	return <div className="mb-4 space-y-3 border-b border-border pb-4">
		<button type="button" aria-pressed={savedOnly} onClick={() => onChange(FAVORITES_TRAIT, savedOnly ? [] : ['saved'])} className="flex min-h-10 w-full items-center justify-between rounded-md border border-border px-3 text-sm hover:bg-accent">
			<span className="inline-flex items-center gap-2"><Bookmark className="size-4" fill={savedOnly ? 'currentColor' : 'none'} />Saved Monkes</span><span>{saved.length}</span>
		</button>
		<p className="text-xs text-muted-foreground">Favorites are saved on this browser.</p>
		<div className="flex flex-wrap items-center gap-2">
			<label htmlFor={colorId} className="text-sm font-medium">Pixel color</label>
			<span className="relative inline-flex h-10 w-12 items-center justify-center rounded border border-border bg-background">
				{!color && <span aria-hidden="true" className="text-muted-foreground">—</span>}
				<input id={colorId} aria-label="Choose a pixel color" type="color" value={color || '#000000'} onChange={(e) => setColor(e.target.value)} className={`absolute inset-0 h-full w-full cursor-pointer rounded bg-background p-1 ${color ? '' : 'opacity-0'}`} />
			</span>
			<button type="button" disabled={!color} onClick={() => color && onChange(COLOR_TRAIT, [color.slice(1)])} className="min-h-10 rounded-md border border-border px-3 text-xs hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50">Find color</button>
		</div>
		{(color || selectedColors.length > 0) && <button type="button" onClick={() => { setColor(null); onChange(COLOR_TRAIT, []) }} className="min-h-9 text-xs text-primary underline">Clear color</button>}
		<p role="status" className="text-xs text-muted-foreground">{selectedColors.length > 0 ? `Filtering by ${selectedColors.map((c) => `#${c}`).join(', ')}` : color ? 'Color selected — choose Find color to apply.' : 'No color filter'}</p>
		<p className="text-xs text-muted-foreground">Matches exact artwork pixels. Click a Monke’s palette to explore its colors.</p>
		<button type="button" className="inline-flex min-h-10 items-center gap-2 text-sm text-primary hover:underline" onClick={async () => {
			try { await navigator.clipboard.writeText(window.location.href); setCopied(true); setError('') }
			catch { setError('Could not copy. Share the URL from your address bar.') }
		}}>{copied ? <Check className="size-4" /> : <LinkIcon className="size-4" />}{copied ? 'Link copied' : 'Copy filtered collection link'}</button>
		{error && <p role="status" className="text-xs text-muted-foreground">{error}</p>}
	</div>
}
